import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Page, PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { money, sentimentColor, timeAgo } from "@/lib/format";
import { UsersThree, CurrencyDollar, Star, EnvelopeOpen, ChatCircleDots, WarningCircle, Crown } from "@phosphor-icons/react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from "recharts";

const CH = ["#EA580C", "#0F172A", "#3B82F6"];

export default function Dashboard() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data });

  if (isLoading || !data) return <Page><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></Page>;
  const k = data.kpis;
  const pie = [
    { name: "Positive", value: data.sentiment_split.positive, color: "#22C55E" },
    { name: "Neutral", value: data.sentiment_split.neutral, color: "#F59E0B" },
    { name: "Negative", value: data.sentiment_split.negative, color: "#EF4444" },
  ];

  return (
    <Page>
      <PageHeader title="Dashboard" subtitle="Cross-channel guest intelligence at a glance." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard index={0} icon={UsersThree} label="Total Guests" value={k.total_guests} hint={`${k.vip_count} VIP profiles`} accent />
        <StatCard index={1} icon={CurrencyDollar} label="Lifetime Value" value={money(k.lifetime_value)} hint="Across all guests" />
        <StatCard index={2} icon={Star} label="Avg Rating" value={k.avg_rating} hint={`${k.negative_reviews} negative reviews`} />
        <StatCard index={3} icon={EnvelopeOpen} label="Email Open Rate" value={`${k.open_rate}%`} hint="Across sent campaigns" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 rounded-lg border border-border bg-card p-5 fade-up">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading font-semibold">Attributed revenue by channel</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Last 8 weeks · email, chat & voice</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.revenue_trend} margin={{ left: -18, right: 6, top: 6 }}>
              <defs>
                {CH.map((c, i) => (
                  <linearGradient key={i} id={`g${i}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={c} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={c} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEE" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#888" }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#888" }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #eee", fontSize: 12 }} />
              <Area type="monotone" dataKey="email" stroke={CH[0]} strokeWidth={2} fill="url(#g0)" />
              <Area type="monotone" dataKey="chat" stroke={CH[1]} strokeWidth={2} fill="url(#g1)" />
              <Area type="monotone" dataKey="voice" stroke={CH[2]} strokeWidth={2} fill="url(#g2)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-lg border border-border bg-card p-5 fade-up" style={{ animationDelay: "80ms" }}>
          <h3 className="font-heading font-semibold">Guest sentiment</h3>
          <p className="text-xs text-muted-foreground mt-0.5 mb-2">From reviews & surveys</p>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={pie} dataKey="value" innerRadius={48} outerRadius={72} paddingAngle={2}>
                {pie.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #eee", fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-1">
            {pie.map((p) => (
              <div key={p.name} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: p.color }} />{p.name}</span>
                <span className="font-medium">{p.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 rounded-lg border border-border bg-card fade-up">
          <div className="flex items-center justify-between p-5 pb-3">
            <h3 className="font-heading font-semibold">Latest reviews</h3>
            <button onClick={() => navigate("/app/reputation")} className="text-xs text-primary font-medium hover:underline">View all →</button>
          </div>
          <div className="divide-y divide-border">
            {data.recent_reviews.map((r) => (
              <div key={r.id} className="flex items-start gap-3 px-5 py-3.5 hover:bg-secondary/40 transition-colors">
                <div className="flex items-center gap-0.5 text-amber-500 shrink-0 pt-0.5">
                  {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={13} weight={i < r.rating ? "fill" : "regular"} className={i < r.rating ? "" : "text-muted-foreground/30"} />)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm truncate">{r.title}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${sentimentColor[r.sentiment]}`}>{r.sentiment}</span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{r.guest_name} · {r.source} · {timeAgo(r.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <StatCard index={0} icon={ChatCircleDots} label="Active Chats" value={k.active_chats} hint="Live concierge sessions" accent />
          <StatCard index={1} icon={WarningCircle} label="Escalated" value={k.escalated_chats} hint="Awaiting human handoff" />
          <StatCard index={2} icon={Crown} label="VIP Guests" value={k.vip_count} hint="High lifetime value" />
        </div>
      </div>
    </Page>
  );
}
